
import { getContract } from "@/api/contract";
import { getRenewal } from "@/api/renewal";
import router from "@/router";
import { MutationTypes, useStore } from "@/store";
import ErrorField from "common/src/components/ErrorField.vue";
import { computed, defineComponent, reactive, ref, UnwrapRef } from "vue";
import { SCR131Form, SCR131FormValidateRule } from "@/forms/scr131";
import useVuelidate from "@vuelidate/core";
import constants from "common/src/constants";
import { scrollToError } from "common/src/utils/screen";
import { ConfirmRequest, postConfirmRequest } from "@/api/confirm";
import axios from "axios";
import { setErrorMessage } from "common/src/utils/validate";
import { ErrorResponse } from "common/src/utils/request";

export default defineComponent({
  components: {
    ErrorField,
  },
  setup() {
    const importantRead = ref(false);
    const mailFormVisible = ref(false);
    // フォーム変数作成
    const form: UnwrapRef<SCR131Form> = reactive({
      tf_rejyjk: false,
      target_verify: false,
      earthquake_verify: false,
      notification_verify: false,
      another_contract: undefined,
      tf_rekyma: undefined,
    });
    const store = useStore();

    getContract().then((con) => {
      getRenewal().then((ren) => {
        if (ren.data.rnw01p?.tf_rekzis === constants.KEIZOKU_ISHI.ARI) {
          // 継続意思有の場合は404ページへ
          router.push({ name: "404" });
        } else if (con.data.kei01p?.tf_kehscd === ren.data.rnw01p?.tf_rehscd) {
          // 契約データの商品コードと更新データの商品コードが一致する場合は重要事項説明は不要
          store.commit(MutationTypes.SET_NEED_DISCLOSURE, false);
          router.push({ name: "Renewal" });
          return;
        } else {
          // 更新データにメールアドレスがある場合は、メールフォームは非表示
          mailFormVisible.value =
            ren.data.rnw01p?.tf_rekyma === undefined || ren.data.rnw01p?.tf_rekyma === null || ren.data?.rnw01p.tf_rekyma === "";

          // ストアの確認が必要かどうかによって処理を分岐
          if (store.state.needDisclosure === undefined) {
            store.commit(MutationTypes.SET_NEED_DISCLOSURE, true);
          } else if (!store.state.needDisclosure) {
            // 一度、確認を行っている場合は値を復元
            form.tf_rejyjk = true;
            form.target_verify = true;
            form.earthquake_verify = true;
            form.notification_verify = true;
            form.another_contract = store.state.hasAnother;

            if (store.state.mail) {
              form.tf_rekyma = store.state.mail;
            }
          }
        }
      });
    });

    // 全体エラーメッセージ
    const g$: string[] = reactive([]);

    // フィールドエラーを外部からセットする変数
    const $externalResults = ref({});
    // フィールドエラー設定初期化
    const f$ = useVuelidate(SCR131FormValidateRule, form, {
      $externalResults,
    });
    // 意向確認のエラーを１つだけ表示
    const intentionError = computed(() => {
      if (f$.value.target_verify.$error || f$.value.earthquake_verify.$error || f$.value.notification_verify.$error) {
        return [...f$.value.target_verify.$errors, ...f$.value.earthquake_verify.$errors, ...f$.value.notification_verify.$errors][0].$message;
      }
      return "";
    });

    // 本人確認リクエストのメールを送付します
    const send = async () => {
      f$.value.$clearExternalResults();

      // メールアドレスが必須なのは本人確認リクエストを行う場合のみなので手動チェック
      if (form.tf_rekyma === "" || form.tf_rekyma === null || form.tf_rekyma === undefined) {
        $externalResults.value = { tf_rekyma: "メールアドレスを入力してください" };
      }
      f$.value.$touch();
      if (f$.value.$invalid) {
        scrollToError();
        return;
      }

      try {
        // API0210呼び出し
        const req: ConfirmRequest = {
          mailAddress: form.tf_rekyma ?? "",
        };
        const res = await postConfirmRequest(req);

        // ストアへシリアル番号をCommit
        store.commit(MutationTypes.SET_RENEWAL_SERIAL, res.serialNo);
        store.commit(MutationTypes.SET_NEED_DISCLOSURE, false);
        store.commit(MutationTypes.SET_ANOTHER, form.another_contract);
        store.commit(MutationTypes.SET_MAIL, form.tf_rekyma);

        // SCR132 確認メール送信結果画面へ遷移
        router.push({ name: "Confirm" });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 400) {
          setErrorMessage(g$, $externalResults, err.response?.data as ErrorResponse);
        }
      }
    };

    // メールアドレスがない方・更新内容入力へ進むをクリック時
    const noMail = () => {
      f$.value.$clearExternalResults();
      f$.value.$touch();
      if (f$.value.$invalid) return;

      store.commit(MutationTypes.SET_NEED_DISCLOSURE, false);
      store.commit(MutationTypes.SET_RENEWAL_SERIAL, "");
      store.commit(MutationTypes.SET_ANOTHER, form.another_contract);

      // SCR130更新内容入力画面へ移動
      router.push({ name: "Renewal" });
    };

    return {
      importantRead,
      mailFormVisible,
      form,
      g$,
      f$,
      intentionError,
      constants,
      send,
      noMail,
    };
  },
});
